import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, ArrowLeft, Check, User, Calendar, Ruler, Weight, Utensils, MapPin, Dumbbell, Moon, Brain, Heart, Pill, AlertCircle, Target, Briefcase, Droplets, Mail, Phone, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import PaymentGate from '@/components/PaymentGate';
import PhoneInputCustom from '@/components/PhoneInputCustom';
import { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';

// FBM Logo URL
const FBM_LOGO_URL = "https://customer-assets.emergentagent.com/job_plan-generator-6/artifacts/6or5u7no_Fit%20Bharat%20Mission%20Logo.png";

// Hindi translations for all questions
const HINDI_TRANSLATIONS = {
  'name': { question: "आपका नाम क्या है?", placeholder: "अपना पूरा नाम दर्ज करें" },
  'email': { question: "आपका ईमेल क्या है?", placeholder: "your.email@example.com" },
  'mobile': { question: "आपका मोबाइल नंबर?", placeholder: "+91 98765 43210" },
  'date_of_birth': { question: "आपकी जन्म तिथि?" },
  'gender': { 
    question: "आपका लिंग?",
    options: { 'Male': 'पुरुष', 'Female': 'महिला', 'Other': 'अन्य' }
  },
  'occupation': { 
    question: "आपका व्यवसाय?",
    options: { 
      'Student': 'छात्र', 'Working Professional': 'पेशेवर', 'Business Owner': 'व्यापारी',
      'Homemaker': 'गृहिणी', 'Retired': 'सेवानिवृत्त', 'Other': 'अन्य' 
    }
  },
  'weight': { question: "आपका वर्तमान वजन?" },
  'height': { question: "आपकी ऊंचाई?" },
  'dietary_preferences': { 
    question: "आपका आहार प्रकार?",
    options: {
      'Vegetarian': 'शाकाहारी', 'Non-Vegetarian': 'मांसाहारी', 'Vegan': 'वीगन',
      'Eggetarian': 'अंडाहारी', 'Jain': 'जैन'
    }
  },
  'cuisine_preference': { 
    question: "आपकी भोजन प्राथमिकताएँ?",
    subtitle: "सभी लागू विकल्प चुनें",
    options: {
      'North Indian': 'उत्तर भारतीय',
      'South Indian': 'दक्षिण भारतीय',
      'West Indian': 'पश्चिम भारतीय',
      'East Indian': 'पूर्व भारतीय',
      'All of the above': 'उपरोक्त सभी'
    }
  },
  'activity_level': { 
    question: "आपकी गतिविधि का स्तर?",
    options: {
      'Very Sedentary': 'बहुत निष्क्रिय', 'Sedentary': 'निष्क्रिय', 'Lightly Active': 'हल्का सक्रिय',
      'Moderately Active': 'मध्यम सक्रिय', 'Active': 'सक्रिय', 'Very Active': 'बहुत सक्रिय'
    }
  },
  'sleep_hours': { 
    question: "आप कितने घंटे सोते हैं?",
    options: { '4': '< 5 घंटे', '5': '5-6 घंटे', '7': '7-8 घंटे', '9': '8+ घंटे' }
  },
  'water_intake': { 
    question: "दैनिक पानी का सेवन?",
    options: { '1.5': '< 2L', '2': '2L', '2.5': '2.5L', '3': '3L', '3.5': '3L+' }
  },
  'stress_level': { 
    question: "आपका तनाव स्तर?",
    options: { 'Low': 'कम', 'Mild': 'हल्का', 'Moderate': 'मध्यम', 'High': 'अधिक', 'Very High': 'बहुत अधिक' }
  },
  'health_conditions': { 
    question: "कोई स्वास्थ्य समस्या या चिंता?",
    subtitle: "सभी लागू विकल्प चुनें",
    options: {
      'Obesity / Overweight': 'मोटापा / अधिक वजन',
      'Low energy': 'कम ऊर्जा',
      'Thyroid': 'थायरॉइड',
      'Women health (PCOD/PCOS/Period Issues)': 'महिला स्वास्थ्य (PCOD/PCOS/पीरियड समस्या)',
      'Digestive issues (gas, bloating, acidity, constipation, IBS-type)': 'पाचन समस्या (गैस, सूजन, एसिडिटी, कब्ज)',
      'Diabetes': 'मधुमेह',
      'Blood pressure (High or Low)': 'रक्तचाप (उच्च या निम्न)',
      'Heart health concerns': 'हृदय स्वास्थ्य चिंता',
      'Fatty liver / Liver issues': 'फैटी लीवर / लीवर समस्या',
      'Kidney issue': 'किडनी समस्या',
      'Joint pain / Body pain': 'जोड़ों का दर्द / शरीर दर्द',
      'Sleep issues': 'नींद की समस्या',
      'Hair fall & Skin issue': 'बालों का झड़ना और त्वचा समस्या',
      'Low Immunity': 'कम प्रतिरोधक क्षमता',
      'Respiratory issue': 'श्वसन समस्या',
      'Stress and Anxiety': 'तनाव और चिंता',
      'Psoriasis': 'सोरायसिस',
      'None': 'कोई नहीं'
    }
  },
  'allergies': { 
    question: "कोई खाद्य एलर्जी?",
    subtitle: "सभी लागू विकल्प चुनें",
    options: {
      'None': 'कोई नहीं', 'Gluten': 'ग्लूटेन', 'Lactose': 'लैक्टोज', 'Nuts': 'मेवे',
      'Soy': 'सोया', 'Eggs': 'अंडे', 'Seafood': 'समुद्री भोजन', 'Caffeine': 'कैफीन'
    }
  },
  'medications': { question: "वर्तमान दवाइयां?", placeholder: "आप जो भी दवाइयां ले रहे हैं उनकी सूची (या खाली छोड़ें)" },
  'health_goals': { 
    question: "आपके प्रमुख स्वास्थ्य लक्ष्य?",
    subtitle: "सभी लागू विकल्प चुनें",
    options: {
      'Fat Loss': 'वजन घटाना', 'Weight Gain': 'वजन बढ़ाना', 'Muscle Building': 'मांसपेशियां बनाना',
      'Gut Healing': 'पाचन सुधार', 'Hormonal Balance': 'हार्मोनल संतुलन', 'Disease Management': 'रोग प्रबंधन',
      'Better Energy': 'ऊर्जा और प्रतिरक्षा', 'Skin & Hair': 'त्वचा और बाल', 'General Wellness': 'सामान्य कल्याण'
    }
  }
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Question configuration - 18 questions
const QUESTIONS = [
  { 
    id: 'name', 
    type: 'text', 
    question: "What's your name?", 
    placeholder: "Enter your full name", 
    icon: User 
  },
  { 
    id: 'email', 
    type: 'email', 
    question: "What's your email?", 
    placeholder: "your.email@example.com", 
    icon: Mail 
  },
  { 
    id: 'mobile', 
    type: 'phone', 
    question: "Your mobile number?", 
    placeholder: "+91 98765 43210", 
    icon: Phone 
  },
  { 
    id: 'date_of_birth', 
    type: 'date-visual', 
    question: "When were you born?", 
    icon: Calendar 
  },
  { 
    id: 'gender', 
    type: 'visual-select', 
    question: "What's your gender?", 
    icon: User, 
    options: [
      { value: 'Male', label: 'Male', icon: '👨' },
      { value: 'Female', label: 'Female', icon: '👩' }
    ]
  },
  { 
    id: 'occupation', 
    type: 'visual-select', 
    question: "What's your occupation?", 
    icon: Briefcase, 
    options: [
      { value: 'Student', label: 'Student', icon: '📚' },
      { value: 'Professional', label: 'Professional', icon: '💼' },
      { value: 'Business Owner', label: 'Business Owner', icon: '🏢' },
      { value: 'Salaried', label: 'Salaried', icon: '💰' },
      { value: 'Homemaker', label: 'Homemaker', icon: '🏠' },
      { value: 'Mother', label: 'Mother', icon: '👩‍👧' },
      { value: 'Retired', label: 'Retired', icon: '🌴' },
      { value: 'Other', label: 'Other', icon: '👤' }
    ]
  },
  { 
    id: 'weight', 
    type: 'slider', 
    question: "What's your current weight?", 
    min: 30, 
    max: 150, 
    step: 0.5,
    unit: 'kg', 
    icon: Weight 
  },
  { 
    id: 'height', 
    type: 'slider', 
    question: "What's your height?", 
    min: 120, 
    max: 220, 
    step: 1,
    unit: 'cm', 
    icon: Ruler 
  },
  { 
    id: 'dietary_preferences', 
    type: 'visual-select', 
    question: "What's your diet type?", 
    icon: Utensils, 
    options: [
      { value: 'Vegetarian', label: 'Vegetarian', icon: '🥗', desc: 'Plant-based diet' },
      { value: 'Non-Vegetarian', label: 'Non-Veg', icon: '🍗', desc: 'Includes meat' },
      { value: 'Vegan', label: 'Vegan', icon: '🌱', desc: 'No animal products' },
      { value: 'Eggetarian', label: 'Eggetarian', icon: '🥚', desc: 'Veg + Eggs' },
      { value: 'Jain', label: 'Jain', icon: '🙏', desc: 'No root vegetables' }
    ]
  },
  { 
    id: 'cuisine_preference', 
    type: 'multi-select-cuisine', 
    question: "Your cuisine preference?", 
    subtitle: "Select all that apply",
    icon: MapPin, 
    options: [
      { value: 'North Indian', label: 'North Indian', icon: '🫓', desc: 'Punjab, Delhi, Haryana, UP' },
      { value: 'South Indian', label: 'South Indian', icon: '🌴', desc: 'Tamil Nadu, Kerala, Karnataka, AP' },
      { value: 'West Indian', label: 'West Indian', icon: '🥣', desc: 'Gujarati, Rajasthani, Marathi' },
      { value: 'East Indian', label: 'East Indian', icon: '🐟', desc: 'Bengali, Odia, Assamese' },
      { value: 'All of the above', label: 'All of the above', icon: '🍽️', desc: 'Open to all cuisines' }
    ]
  },
  { 
    id: 'activity_level', 
    type: 'visual-select-detailed', 
    question: "Your activity level?", 
    icon: Dumbbell, 
    options: [
      { 
        value: 'Very Sedentary', label: 'Very Sedentary', icon: '🪑', 
        desc: 'No job / retired / old age', 
        details: 'Less than 2,000 steps/day • No regular exercise • Mostly sitting or resting' 
      },
      { 
        value: 'Sedentary', label: 'Sedentary', icon: '💻', 
        desc: 'Desk job or work from home', 
        details: '2,000–3,500 steps/day • No workouts or very occasional • Long sitting hours' 
      },
      { 
        value: 'Lightly Active', label: 'Lightly Active', icon: '🚶', 
        desc: 'Household chores OR light daily movement', 
        details: '3,500–5,000 steps/day • 1–2 light workouts per week • Some movement, not consistent' 
      },
      { 
        value: 'Moderately Active', label: 'Moderately Active', icon: '🏃', 
        desc: 'Active job OR intentional exercise routine', 
        details: '5,000–7,000 steps/day • 2–3 workouts/week (gym, yoga, sports) • Regular activity' 
      },
      { 
        value: 'Active', label: 'Active', icon: '💪', 
        desc: 'Physically active job or structured fitness lifestyle', 
        details: '8,000–10,000 steps/day • 4–5 workouts/week • Fitness is part of your routine' 
      },
      { 
        value: 'Very Active', label: 'Very Active', icon: '🏋️', 
        desc: 'Athlete-level training', 
        details: '10,000+ steps/day • 5–6 intense workouts/week • High physical demand every day' 
      }
    ]
  },
  { 
    id: 'sleep_hours', 
    type: 'visual-select', 
    question: "How many hours do you sleep?", 
    icon: Moon, 
    options: [
      { value: '4', label: '< 5 hrs', icon: '😫', desc: 'Very less' },
      { value: '5', label: '5-6 hrs', icon: '😴', desc: 'Less than ideal' },
      { value: '7', label: '7-8 hrs', icon: '😊', desc: 'Optimal' },
      { value: '9', label: '8+ hrs', icon: '😴', desc: 'Good rest' }
    ]
  },
  { 
    id: 'water_intake', 
    type: 'visual-select', 
    question: "Daily water intake?", 
    icon: Droplets, 
    options: [
      { value: '1.5', label: '< 2L', icon: '💧', desc: 'Less than 2 liters' },
      { value: '2', label: '2L', icon: '💧💧', desc: '2 liters' },
      { value: '2.5', label: '2.5L', icon: '💧💧💧', desc: '2.5 liters' },
      { value: '3', label: '3L', icon: '🌊', desc: '3 liters' },
      { value: '3.5', label: '3L+', icon: '🌊', desc: 'More than 3 liters' }
    ]
  },
  { 
    id: 'stress_level', 
    type: 'visual-scale', 
    question: "Your stress level?", 
    icon: Brain, 
    options: [
      { value: 'Low', label: 'Low', color: 'bg-green-500', emoji: '😌' },
      { value: 'Mild', label: 'Mild', color: 'bg-lime-500', emoji: '🙂' },
      { value: 'Moderate', label: 'Moderate', color: 'bg-yellow-500', emoji: '😐' },
      { value: 'High', label: 'High', color: 'bg-orange-500', emoji: '😟' },
      { value: 'Very High', label: 'Very High', color: 'bg-red-500', emoji: '😰' }
    ]
  },
  { 
    id: 'health_goals', 
    type: 'multi-select', 
    question: "Your primary health goals?", 
    subtitle: "Select all that apply", 
    icon: Target, 
    options: [
      { value: 'Fat Loss', label: 'Fat Loss', icon: '📉' },
      { value: 'Weight Gain', label: 'Weight Gain', icon: '📈' },
      { value: 'Muscle Building', label: 'Muscle Building', icon: '💪' },
      { value: 'Gut Healing', label: 'Gut Healing', icon: '🫃' },
      { value: 'Hormonal Balance', label: 'Hormonal Balance', icon: '⚖️' },
      { value: 'Disease Management', label: 'Disease Support', icon: '🩺' },
      { value: 'Better Energy', label: 'Energy & Immunity', icon: '⚡' },
      { value: 'Skin & Hair', label: 'Skin & Hair', icon: '✨' },
      { value: 'General Wellness', label: 'General Wellness', icon: '🌿' }
    ]
  },
  { 
    id: 'health_conditions', 
    type: 'multi-select', 
    question: "Any Health Concerns or Conditions?", 
    subtitle: "Select all that apply", 
    icon: Heart, 
    options: [
      { value: 'Obesity / Overweight', label: 'Obesity / Overweight', icon: '⚖️' },
      { value: 'Low energy', label: 'Low energy', icon: '😴' },
      { value: 'Thyroid', label: 'Thyroid', icon: '🦋' },
      { value: 'Women health (PCOD/PCOS/Period Issues)', label: 'Women health (PCOD/PCOS/Period Issues)', icon: '👩' },
      { value: 'Digestive issues (gas, bloating, acidity, constipation, IBS-type)', label: 'Digestive issues', icon: '🫃' },
      { value: 'Diabetes', label: 'Diabetes', icon: '🩸' },
      { value: 'Blood pressure (High or Low)', label: 'Blood pressure (High or Low)', icon: '❤️' },
      { value: 'Heart health concerns', label: 'Heart health concerns', icon: '🫀' },
      { value: 'Fatty liver / Liver issues', label: 'Fatty liver / Liver issues', icon: '🫁' },
      { value: 'Kidney issue', label: 'Kidney issue', icon: '🫘' },
      { value: 'Joint pain / Body pain', label: 'Joint pain / Body pain', icon: '🦵' },
      { value: 'Sleep issues', label: 'Sleep issues', icon: '🌙' },
      { value: 'Hair fall & Skin issue', label: 'Hair fall & Skin issue', icon: '💇' },
      { value: 'Low Immunity', label: 'Low Immunity', icon: '🛡️' },
      { value: 'Respiratory issue', label: 'Respiratory issue', icon: '🌬️' },
      { value: 'Stress and Anxiety', label: 'Stress and Anxiety', icon: '🧠' },
      { value: 'Psoriasis', label: 'Psoriasis', icon: '🩹' },
      { value: 'None', label: 'None', icon: '✓' }
    ]
  },
  { 
    id: 'allergies', 
    type: 'multi-select', 
    question: "Any food allergies?", 
    subtitle: "Select all that apply", 
    icon: AlertCircle, 
    options: [
      { value: 'None', label: 'None', icon: '✓' },
      { value: 'Gluten', label: 'Gluten', icon: '🌾' },
      { value: 'Lactose', label: 'Lactose', icon: '🥛' },
      { value: 'Nuts', label: 'Nuts', icon: '🥜' },
      { value: 'Soy', label: 'Soy', icon: '🫛' },
      { value: 'Eggs', label: 'Eggs', icon: '🥚' },
      { value: 'Seafood', label: 'Seafood', icon: '🦐' },
      { value: 'Caffeine', label: 'Caffeine', icon: '☕' }
    ]
  },
  { 
    id: 'medications', 
    type: 'text-optional', 
    question: "Current medications?", 
    placeholder: "List any medications you're taking (or leave blank)", 
    icon: Pill 
  }
];

const Questionnaire = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isHindi, setIsHindi] = useState(false);
  const [showPaymentGate, setShowPaymentGate] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    date_of_birth: '',
    gender: '',
    occupation: '',
    height: 165,
    weight: 65,
    dietary_preferences: '',
    cuisine_preference: [],
    activity_level: '',
    sleep_hours: '',
    water_intake: '',
    stress_level: '',
    health_conditions: [],
    allergies: [],
    medications: '',
    health_goals: []
  });

  const currentQuestion = QUESTIONS[currentIndex];
  // Calculate progress - account for payment gate after Q3
  const totalSteps = QUESTIONS.length + 1; // +1 for payment gate
  const actualStep = showPaymentGate ? 4 : (currentIndex < 3 ? currentIndex + 1 : currentIndex + 2);
  const progress = (actualStep / totalSteps) * 100;

  const updateField = (value) => {
    setFormData(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const toggleMultiSelect = (value) => {
    setFormData(prev => {
      const arr = prev[currentQuestion.id] || [];
      
      // Special handling for "All of the above" in cuisine preference
      if (currentQuestion.id === 'cuisine_preference' && value === 'All of the above') {
        const allCuisineOptions = ['North Indian', 'South Indian', 'West Indian', 'East Indian', 'All of the above'];
        // If already has "All of the above", deselect everything
        if (arr.includes('All of the above')) {
          return { ...prev, [currentQuestion.id]: [] };
        }
        // Otherwise select all options
        return { ...prev, [currentQuestion.id]: allCuisineOptions };
      }
      
      // For cuisine preference, if selecting individual option, remove "All of the above"
      if (currentQuestion.id === 'cuisine_preference' && value !== 'All of the above') {
        const filteredArr = arr.filter(v => v !== 'All of the above');
        if (filteredArr.includes(value)) {
          return { ...prev, [currentQuestion.id]: filteredArr.filter(v => v !== value) };
        }
        return { ...prev, [currentQuestion.id]: [...filteredArr, value] };
      }
      
      // Handle "None" for health conditions and allergies
      if (value === 'None') {
        return { ...prev, [currentQuestion.id]: arr.includes(value) ? [] : [value] };
      }
      const filteredArr = arr.filter(v => v !== 'None');
      if (filteredArr.includes(value)) {
        return { ...prev, [currentQuestion.id]: filteredArr.filter(v => v !== value) };
      }
      return { ...prev, [currentQuestion.id]: [...filteredArr, value] };
    });
  };

  const canProceed = () => {
    const value = formData[currentQuestion.id];
    if (currentQuestion.type === 'multi-select' || currentQuestion.type === 'multi-select-cuisine') {
      return Array.isArray(value) && value.length > 0;
    }
    if (currentQuestion.type === 'slider') return true;
    if (currentQuestion.type === 'text-optional') return true;
    if (currentQuestion.type === 'email') {
      return value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    if (currentQuestion.type === 'phone') {
      // Use libphonenumber-js for accurate international phone number validation
      // isValidPhoneNumber checks if the number is valid for the detected country
      // isPossiblePhoneNumber is more lenient - checks if length is possible
      if (!value) return false;
      try {
        // First check if it's a possible phone number (lenient check)
        if (!isPossiblePhoneNumber(value)) return false;
        // Then check if it's a valid phone number (strict check)
        return isValidPhoneNumber(value);
      } catch (e) {
        // If validation throws, fall back to basic length check
        return value.length >= 8;
      }
    }
    return value && value.toString().trim() !== '';
  };

  const nextQuestion = () => {
    // After mobile (Q3, index 2), show payment gate
    if (currentIndex === 2 && !orderId) {
      setDirection(1);
      setShowPaymentGate(true);
      return;
    }
    
    if (currentIndex < QUESTIONS.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const prevQuestion = () => {
    if (showPaymentGate) {
      setShowPaymentGate(false);
      setDirection(-1);
      return;
    }
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handlePaymentSuccess = (newOrderId) => {
    setOrderId(newOrderId);
    setShowPaymentGate(false);
    setDirection(1);
    setCurrentIndex(3); // Move to Q4 (DOB)
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && canProceed()) {
      nextQuestion();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        occupation: formData.occupation,
        height: parseFloat(formData.height) || 0,
        weight: parseFloat(formData.weight) || 0,
        dietary_preferences: formData.dietary_preferences,
        cuisine_preference: Array.isArray(formData.cuisine_preference) 
          ? formData.cuisine_preference.join(', ') 
          : formData.cuisine_preference,
        activity_level: formData.activity_level,
        sleep_hours: parseFloat(formData.sleep_hours) || 7,
        water_intake: parseFloat(formData.water_intake) || 2,
        stress_level: formData.stress_level,
        health_conditions: (formData.health_conditions || []).filter(v => v !== 'None'),
        health_goals: formData.health_goals || [],
        allergies: (formData.allergies || []).filter(v => v !== 'None'),
        medications: formData.medications || '',
        current_diet_description: '',
        exercise_routine: formData.activity_level || ''
      };
      
      // Include order_id if payment was made
      const url = orderId 
        ? `${API}/participants?order_id=${orderId}` 
        : `${API}/participants`;
      
      const response = await axios.post(url, payload);
      const participantId = response.data.id;
      
      // Store in sessionStorage as backup for page refresh
      sessionStorage.setItem('lastParticipantId', participantId);
      sessionStorage.setItem('lastParticipantName', formData.name);
      
      // Navigate to thank you page with participant data for PDF generation
      // Include participantId in URL for reliability (survives page refresh)
      navigate(`/thank-you?pid=${participantId}`, { 
        state: { 
          participantId: participantId,
          participantName: formData.name,
          pdfReady: false 
        }
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderQuestion = () => {
    switch (currentQuestion.type) {
      case 'text':
      case 'text-optional':
        return (
          <div className="space-y-4 md:space-y-6">
            {/* Subtitle/hint for mobile readability */}
            {currentQuestion.type === 'text-optional' && (
              <p className="text-sm md:text-base text-gray-500 text-center px-2 leading-relaxed">
                {isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.placeholder 
                  ? HINDI_TRANSLATIONS[currentQuestion.id].placeholder 
                  : currentQuestion.placeholder}
              </p>
            )}
            <Input
              type="text"
              placeholder={currentQuestion.type === 'text-optional' 
                ? (isHindi ? 'टाइप करें या खाली छोड़ें...' : 'Type here or leave blank...')
                : (isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.placeholder 
                  ? HINDI_TRANSLATIONS[currentQuestion.id].placeholder 
                  : currentQuestion.placeholder)}
              value={formData[currentQuestion.id]}
              onChange={(e) => updateField(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-14 md:h-16 text-lg md:text-xl text-center rounded-2xl border-2 border-[#E5E0D1] focus:border-[#1A4D2E] bg-white"
              autoFocus
              data-testid={`input-${currentQuestion.id}`}
            />
          </div>
        );

      case 'email':
        return (
          <div className="space-y-6">
            <Input
              type="email"
              placeholder={isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.placeholder 
                ? HINDI_TRANSLATIONS[currentQuestion.id].placeholder 
                : currentQuestion.placeholder}
              value={formData[currentQuestion.id]}
              onChange={(e) => updateField(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-16 text-xl text-center rounded-2xl border-2 border-[#E5E0D1] focus:border-[#1A4D2E] bg-white"
              autoFocus
              data-testid={`input-${currentQuestion.id}`}
            />
            {formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
              <p className="text-red-500 text-sm text-center">
                {isHindi ? 'कृपया एक वैध ईमेल पता दर्ज करें' : 'Please enter a valid email address'}
              </p>
            )}
          </div>
        );

      case 'phone':
        return (
          <div className="space-y-6">
            <PhoneInputCustom
              value={formData[currentQuestion.id]}
              onChange={(value) => updateField(value || '')}
              defaultCountry="IN"
              placeholder="Enter phone number"
              data-testid={`input-${currentQuestion.id}`}
            />
            <p className="text-sm text-gray-400 text-center">
              {isHindi ? 'देश बदलने के लिए झंडे पर क्लिक करें' : 'Click on flag to change country'}
            </p>
          </div>
        );

      case 'date-visual':
        return (
          <div className="space-y-3 md:space-y-4">
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              <div className="space-y-1 md:space-y-2">
                <label className="text-xs md:text-sm text-gray-500 text-center block">{isHindi ? 'दिन' : 'Day'}</label>
                <select
                  value={formData.date_of_birth?.split('-')[2] || ''}
                  onChange={(e) => {
                    const parts = (formData.date_of_birth || '--').split('-');
                    updateField(`${parts[0] || '1990'}-${parts[1] || '01'}-${e.target.value.padStart(2, '0')}`);
                  }}
                  className="w-full h-12 md:h-14 text-base md:text-lg text-center rounded-xl border-2 border-[#E5E0D1] focus:border-[#1A4D2E] bg-white cursor-pointer touch-manipulation"
                  data-testid="input-day"
                >
                  <option value="">--</option>
                  {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                    <option key={d} value={d.toString().padStart(2, '0')}>{d}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:space-y-2">
                <label className="text-xs md:text-sm text-gray-500 text-center block">{isHindi ? 'महीना' : 'Month'}</label>
                <select
                  value={formData.date_of_birth?.split('-')[1] || ''}
                  onChange={(e) => {
                    const parts = (formData.date_of_birth || '--').split('-');
                    updateField(`${parts[0] || '1990'}-${e.target.value}-${parts[2] || '01'}`);
                  }}
                  className="w-full h-12 md:h-14 text-base md:text-lg text-center rounded-xl border-2 border-[#E5E0D1] focus:border-[#1A4D2E] bg-white cursor-pointer touch-manipulation"
                  data-testid="input-month"
                >
                  <option value="">--</option>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                    <option key={m} value={(i + 1).toString().padStart(2, '0')}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1 md:space-y-2">
                <label className="text-xs md:text-sm text-gray-500 text-center block">{isHindi ? 'वर्ष' : 'Year'}</label>
                <select
                  value={formData.date_of_birth?.split('-')[0] || ''}
                  onChange={(e) => {
                    const parts = (formData.date_of_birth || '--').split('-');
                    updateField(`${e.target.value}-${parts[1] || '01'}-${parts[2] || '01'}`);
                  }}
                  className="w-full h-12 md:h-14 text-base md:text-lg text-center rounded-xl border-2 border-[#E5E0D1] focus:border-[#1A4D2E] bg-white cursor-pointer touch-manipulation"
                  data-testid="input-year"
                >
                  <option value="">--</option>
                  {Array.from({length: 80}, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case 'visual-select':
        return (
          <div className={cn(
            "grid gap-2 md:gap-3",
            currentQuestion.options.length === 2 ? "grid-cols-2 max-w-xs mx-auto" :
            currentQuestion.options.length <= 3 ? "grid-cols-2 xs:grid-cols-3" : 
            currentQuestion.options.length === 4 ? "grid-cols-2" : 
            "grid-cols-2 sm:grid-cols-3"
          )}>
            {currentQuestion.options.map((option) => {
              const hindiLabel = isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.options?.[option.value];
              return (
                <motion.button
                  key={option.value}
                  type="button"
                  onClick={() => updateField(option.value)}
                  className={cn(
                    "p-3 md:p-5 rounded-xl md:rounded-2xl border-2 text-center group min-h-[80px] md:min-h-[100px] touch-manipulation active:scale-[0.98]",
                    formData[currentQuestion.id] === option.value
                      ? "border-[#1A4D2E] bg-[#1A4D2E]/5 shadow-lg"
                      : "border-[#E5E0D1] bg-white active:border-[#1A4D2E]/50"
                  )}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`option-${option.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                >
                  <span className="text-2xl md:text-3xl mb-1 md:mb-2 block">{option.icon}</span>
                  <span className={cn(
                    "text-xs md:text-sm font-medium block leading-tight",
                    formData[currentQuestion.id] === option.value ? "text-[#1A4D2E]" : "text-gray-700"
                  )}>
                    {hindiLabel || option.label}
                  </span>
                  {option.desc && (
                    <span className="text-[10px] md:text-xs text-gray-400 mt-0.5 md:mt-1 block leading-tight hidden sm:block">{option.desc}</span>
                  )}
                </motion.button>
              );
            })}
          </div>
        );

      case 'visual-select-activity':
      case 'visual-select-detailed':
        return (
          <div className="grid grid-cols-1 gap-2 md:gap-3 max-h-[55vh] md:max-h-[60vh] overflow-y-auto pr-1 md:pr-2 -mr-1 md:-mr-2">
            {currentQuestion.options.map((option) => {
              const hindiLabel = isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.options?.[option.value];
              return (
                <motion.button
                  key={option.value}
                  type="button"
                  onClick={() => updateField(option.value)}
                  className={cn(
                    "p-4 rounded-2xl border-2 transition-all text-left",
                    formData[currentQuestion.id] === option.value
                      ? "border-[#1A4D2E] bg-[#1A4D2E]/5 shadow-lg"
                      : "border-[#E5E0D1] hover:border-[#1A4D2E]/50 bg-white"
                  )}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  data-testid={`option-${option.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{option.icon}</span>
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "text-sm font-semibold block",
                        formData[currentQuestion.id] === option.value ? "text-[#1A4D2E]" : "text-gray-800"
                      )}>
                        {hindiLabel || option.label}
                      </span>
                      <span className="text-xs text-gray-500 block mt-0.5">{option.desc}</span>
                      {option.details && (
                        <span className="text-xs text-gray-400 block mt-1 leading-relaxed">{option.details}</span>
                      )}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        );

      case 'visual-scale':
        return (
          <div className="flex justify-center gap-3">
            {currentQuestion.options.map((option, index) => {
              const hindiLabel = isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.options?.[option.value];
              return (
                <motion.button
                  key={option.value}
                  type="button"
                  onClick={() => updateField(option.value)}
                  className={cn(
                    "w-16 h-20 rounded-2xl border-2 transition-all flex flex-col items-center justify-center",
                    formData[currentQuestion.id] === option.value
                      ? `${option.color} border-transparent text-white shadow-lg`
                      : "border-[#E5E0D1] bg-white hover:border-[#1A4D2E]/50"
                  )}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`scale-${option.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                >
                  <span className="text-2xl mb-1">{option.emoji}</span>
                  <span className="text-xs font-medium">{hindiLabel || option.label}</span>
                </motion.button>
              );
            })}
          </div>
        );

      case 'slider':
        const sliderValue = formData[currentQuestion.id] || currentQuestion.min;
        return (
          <div className="space-y-8">
            <div className="text-center">
              <span className="text-6xl font-bold text-[#1A4D2E]">{sliderValue}</span>
              <span className="text-2xl text-gray-400 ml-2">{currentQuestion.unit}</span>
            </div>
            <div className="px-4">
              <input
                type="range"
                min={currentQuestion.min}
                max={currentQuestion.max}
                step={currentQuestion.step || 1}
                value={sliderValue}
                onChange={(e) => updateField(parseFloat(e.target.value))}
                className="w-full h-3 bg-[#E5E0D1] rounded-full appearance-none cursor-pointer slider-thumb touch-manipulation"
                data-testid={`slider-${currentQuestion.id}`}
              />
              <div className="flex justify-between text-xs md:text-sm text-gray-400 mt-2">
                <span>{currentQuestion.min} {currentQuestion.unit}</span>
                <span>{currentQuestion.max} {currentQuestion.unit}</span>
              </div>
            </div>
          </div>
        );

      case 'multi-select':
        return (
          <div className="space-y-3 md:space-y-4">
            {currentQuestion.subtitle && (
              <p className="text-center text-gray-500 text-xs md:text-sm">
                {isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.subtitle 
                  ? HINDI_TRANSLATIONS[currentQuestion.id].subtitle 
                  : currentQuestion.subtitle}
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3 max-h-[50vh] overflow-y-auto pr-1 -mr-1">
              {currentQuestion.options.map((option) => {
                const isSelected = (formData[currentQuestion.id] || []).includes(option.value);
                const hindiLabel = isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.options?.[option.value];
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => toggleMultiSelect(option.value)}
                    className={cn(
                      "p-2.5 md:p-4 rounded-xl border-2 text-center min-h-[70px] md:min-h-[80px] touch-manipulation active:scale-[0.98]",
                      isSelected
                        ? "border-[#D2E823] bg-[#D2E823]/10"
                        : "border-[#E5E0D1] bg-white active:border-[#D2E823]/50"
                    )}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`multi-${option.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  >
                    <span className="text-lg md:text-xl mb-0.5 md:mb-1 block">{option.icon}</span>
                    <span className={cn(
                      "text-[10px] md:text-xs font-medium leading-tight block",
                      isSelected ? "text-[#1A4D2E]" : "text-gray-600"
                    )}>
                      {hindiLabel || option.label}
                    </span>
                    {isSelected && (
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-[#D2E823] mx-auto mt-0.5 md:mt-1" />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        );

      case 'multi-select-cuisine':
        return (
          <div className="space-y-3 md:space-y-4">
            {currentQuestion.subtitle && (
              <p className="text-center text-gray-500 text-xs md:text-sm">
                {isHindi && HINDI_TRANSLATIONS[currentQuestion.id]?.subtitle 
                  ? HINDI_TRANSLATIONS[currentQuestion.id].subtitle 
                  : currentQuestion.subtitle}
              </p>
            )}
            <div className="grid grid-cols-1 gap-2 md:gap-3">
              {currentQuestion.options.map((option) => {
                const isSelected = (formData[currentQuestion.id] || []).includes(option.value);
                return (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => toggleMultiSelect(option.value)}
                    className={cn(
                      "p-5 rounded-2xl border-2 transition-all text-left",
                      isSelected
                        ? "border-[#D2E823] bg-[#D2E823]/10"
                        : "border-[#E5E0D1] bg-white hover:border-[#D2E823]/50"
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`cuisine-${option.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-3xl">{option.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            "font-medium",
                            isSelected ? "text-[#1A4D2E]" : "text-gray-700"
                          )}>
                            {option.label}
                          </span>
                          {isSelected && (
                            <Check className="w-5 h-5 text-[#D2E823]" />
                          )}
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">{option.desc}</span>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const Icon = currentQuestion?.icon;

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-[#F9F8F6] to-white flex flex-col">
      {/* Header - Mobile optimized sticky */}
      <div className="sticky top-0 z-50 px-4 md:px-6 py-3 md:py-4 bg-[#F9F8F6]/95 backdrop-blur-md border-b border-[#E5E0D1]/50">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <img 
              src={FBM_LOGO_URL} 
              alt="Fit Bharat Mission" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-[#D2E823]"
            />
            <span className="font-serif text-base md:text-lg font-bold text-[#1A4D2E] hidden xs:block">Fit Bharat Mission</span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Hindi/English Toggle - Compact on mobile */}
            <button
              onClick={() => setIsHindi(!isHindi)}
              className={cn(
                "flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-2 rounded-full text-xs md:text-sm font-medium transition-colors min-h-[44px] min-w-[44px] justify-center touch-manipulation",
                isHindi 
                  ? "bg-[#FF9933] text-white" 
                  : "bg-[#E5E0D1] text-gray-600 active:bg-[#D2E823]/30"
              )}
              data-testid="lang-toggle"
            >
              <Languages className="w-3.5 h-3.5 md:w-4 md:h-4" />
              {isHindi ? 'EN' : 'हि'}
            </button>
            <span className="text-xs md:text-sm text-gray-500 bg-white/80 px-2 py-1 rounded-full">
              {showPaymentGate ? '4' : (currentIndex < 3 ? currentIndex + 1 : currentIndex + 2)}/{QUESTIONS.length + 1}
            </span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="max-w-2xl mx-auto mt-2 md:mt-3">
          <div className="h-1.5 md:h-2 bg-[#E5E0D1] rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-[#D2E823]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Question or Payment Gate - Mobile optimized spacing */}
      <div className="flex-1 flex items-start md:items-center justify-center px-4 md:px-6 py-6 md:py-8 overflow-y-auto">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait" initial={false}>
            {showPaymentGate ? (
              <motion.div
                key="payment-gate"
                initial={{ opacity: 0, x: direction * 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -50 }}
                transition={{ duration: 0.3 }}
              >
                <PaymentGate
                  userData={{
                    name: formData.name,
                    email: formData.email,
                    mobile: formData.mobile
                  }}
                  onSuccess={handlePaymentSuccess}
                  onBack={prevQuestion}
                  isHindi={isHindi}
                />
              </motion.div>
            ) : (
              <motion.div
                key={`${currentIndex}-${isHindi}`}
                initial={{ opacity: 0, x: direction * 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -50 }}
                transition={{ duration: 0.3 }}
                className="space-y-5 md:space-y-8"
              >
                {/* Icon - Smaller on mobile */}
                <div className="flex justify-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#1A4D2E] rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg">
                    {Icon && <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />}
                  </div>
                </div>

                {/* Question text - Responsive sizing */}
                <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] text-center px-2">
                  {isHindi && HINDI_TRANSLATIONS[currentQuestion?.id]?.question 
                    ? HINDI_TRANSLATIONS[currentQuestion.id].question 
                    : currentQuestion?.question}
                </h2>

                {/* Input */}
                {renderQuestion()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation - Fixed bottom on mobile for easy thumb access */}
      {!showPaymentGate && (
        <div className="sticky bottom-0 px-4 md:px-6 py-4 md:py-6 border-t border-[#E5E0D1] bg-white/95 backdrop-blur-md safe-area-pb">
          <div className="max-w-2xl mx-auto flex justify-between items-center gap-3">
            <Button
              variant="ghost"
              onClick={prevQuestion}
              disabled={currentIndex === 0}
              className="rounded-full h-12 md:h-14 px-4 md:px-6 min-w-[100px] touch-manipulation"
              data-testid="btn-prev"
            >
              <ArrowLeft className="w-5 h-5 mr-1 md:mr-2" />
              {isHindi ? 'पीछे' : 'Back'}
            </Button>

            <Button
              onClick={nextQuestion}
              disabled={!canProceed() || isSubmitting}
              className={cn(
                "rounded-full h-12 md:h-14 px-6 md:px-8 min-w-[120px] text-base font-medium touch-manipulation active:scale-[0.98]",
                canProceed() 
                  ? "bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 text-white shadow-lg" 
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              )}
              data-testid="btn-next"
            >
              {isSubmitting ? (
                <>
                  <div className="spinner mr-2" />
                  {isHindi ? 'जमा हो रहा है...' : 'Submitting...'}
                </>
              ) : currentIndex === QUESTIONS.length - 1 ? (
                <>
                  {isHindi ? 'जमा करें' : 'Submit'}
                  <Check className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  {isHindi ? 'आगे' : 'Next'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Custom slider styles - Larger touch targets */}
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 32px;
          height: 32px;
          background: #1A4D2E;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        }
        .slider-thumb::-moz-range-thumb {
          width: 32px;
          height: 32px;
          background: #1A4D2E;
          border-radius: 50%;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        }
        @media (min-width: 768px) {
          .slider-thumb::-webkit-slider-thumb {
            width: 28px;
            height: 28px;
          }
          .slider-thumb::-moz-range-thumb {
            width: 28px;
            height: 28px;
          }
        }
        .safe-area-pb {
          padding-bottom: max(16px, env(safe-area-inset-bottom));
        }
      `}</style>
    </div>
  );
};

export default Questionnaire;
